import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useTheme } from "@mui/material";
import { IRoadwayConfig } from "types";
import imgLampRopeReference from "assets/images/lamp-rope-reference.png";
import imgLuminaireReference from "assets/images/lamp-reference.png";
import getStyles from "./styles";
import { roundToTwo } from "utils/helpers";

type Props = {
  roadwayConfig: IRoadwayConfig;
  delta: string;
  position: string;
  numberOfLuminaires: number | null;
  lph: string;
  overhang: string;
  tilt: string;
  maintenanceFactor: number;
};

const LuminaireRow: FC<Props> = ({ roadwayConfig, delta, position, numberOfLuminaires, lph, overhang, tilt, maintenanceFactor }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).LuminaireRow, [theme]);

  return (
    <Box sx={styles.container}>
      <Typography variant="h2" sx={styles.title}>
        {t("Luminaire row")}
      </Typography>
      <TableContainer component={Paper} sx={styles.card}>
        <Box component="img" src={position === "rope" ? imgLampRopeReference : imgLuminaireReference} sx={styles.image} />

        <Table padding="none" sx={styles.table}>
          <TableBody>
            <TableRow>
              <TableCell>{t("Distance between poles")}</TableCell>
              <TableCell>{delta} m</TableCell>
            </TableRow>

            {numberOfLuminaires && (
              <TableRow>
                <TableCell>
                  {t("Number of luminaires")} ({roadwayConfig.roadLength} km)
                </TableCell>
                <TableCell>{numberOfLuminaires}</TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell>
                {t("Height of the light point")} <Box sx={styles.chip}>H</Box>
              </TableCell>
              <TableCell>{lph} m</TableCell>
            </TableRow>

            {position !== "rope" && (
              <>
                <TableRow>
                  <TableCell>
                    {t("Overhang")} <Box sx={styles.chip}>A</Box>
                  </TableCell>
                  <TableCell>{roundToTwo(Number.parseFloat(overhang))} m</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("Tilt of the luminaire")} <Box sx={styles.chip}>T</Box>
                  </TableCell>
                  <TableCell>{tilt}&deg;</TableCell>
                </TableRow>
              </>
            )}

            <TableRow>
              <TableCell>{t("Maintenance factor")}</TableCell>
              <TableCell>{maintenanceFactor}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LuminaireRow;
